import { BINDING_HEADER, browserAuthPath } from '@/lib/guest-auth/constants';
import type { GatedCall } from '@/lib/guest-auth/types';
import { noteSession, type SessionNote } from '@/lib/history-key/session';
import { safeLocalStorage } from '@/lib/storage/safe-storage';
import { fetchWithTimeout } from '@/lib/utils/api-client';
import { isWebsiteBrowser } from '@/lib/website-auth/browser';
import { WEBSITE_SESSION_TIMEOUT_MS } from '@/lib/website-auth/constants';

// WORKAROUND: Keep a partitioned binding copy for embedded browsers that drop the cookie.
// The chat already receives the bearer; remove this fallback if authentication moves entirely server-side.
function bindingKey(tenant: string): string {
  return `bazak-guest-auth-binding:${tenant}`;
}

function rememberBinding({ tenant, binding }: { tenant: string; binding: string }): void {
  try {
    localStorage.setItem(bindingKey(tenant), binding);
  } catch {
    // Storage refused: the cookie is the only carrier here.
  }
}

// WORKAROUND: Send the stored binding when embedded browsers drop cookies.
// Remove with rememberBinding and the readBinding header fallback when auth is entirely server-side.
function bindingHeaders(tenant: string): Record<string, string> {
  try {
    const binding = localStorage.getItem(bindingKey(tenant));
    return binding ? { [BINDING_HEADER]: binding } : {};
  } catch {
    return {};
  }
}

export interface SessionLookup {
  signedIn?: boolean;
  accessToken?: string;
  /** `user-<hash>`: who the browser's account conversations belong to. */
  scope?: string;
  /** Base64url AES key for that owner's conversations; held in memory only. */
  historyKey?: string;
  /** A newer lookup started before this one answered; the answer describes the past. */
  superseded?: boolean;
  /** Website auth only: why the browser is signed out (a `WEBSITE_ERROR` code). */
  reason?: string;
}

let latestLookup = 0;
let inflight: { lookup: number; tenant: string; promise: Promise<SessionLookup> } | undefined;

/** Reads the session. Concurrent callers share one request, so only a sign-out meanwhile can
 *  make an answer stale, and only the most recently started lookup gets to change the held key.
 *  A `fresh` lookup never joins one in flight: that request may predate the change it asks about. */
export function lookupSession({
  tenant,
  fresh = false,
}: {
  tenant: string;
  fresh?: boolean;
}): Promise<SessionLookup> {
  if (!fresh && inflight?.lookup === latestLookup && inflight.tenant === tenant)
    return inflight.promise;
  const lookup = ++latestLookup;
  const promise = readSession({ tenant, lookup }).finally(() => {
    if (inflight?.lookup === lookup) inflight = undefined;
  });
  inflight = { lookup, tenant, promise };
  return promise;
}

async function readSession({ tenant, lookup }: { tenant: string; lookup: number }) {
  const response = await fetchWithTimeout({
    url: browserAuthPath({ tenant, action: 'token' }),
    options: { cache: 'no-store', headers: bindingHeaders(tenant) },
    timeout: WEBSITE_SESSION_TIMEOUT_MS,
    retries: 0,
  });
  function superseded() {
    return lookup !== latestLookup;
  }
  function note(body: SessionNote) {
    return superseded() ? undefined : noteSession(body);
  }
  function answer(session: SessionLookup): SessionLookup {
    return superseded() ? { ...session, superseded: true } : session;
  }
  if (response.status === 401 && isWebsiteBrowser(tenant)) {
    await note(null);
    return answer({ signedIn: false });
  }
  if (!response.ok) throw new Error(`Session lookup failed with ${response.status}`);
  const session: SessionLookup = await response.json();
  try {
    await note(session);
  } catch (error) {
    if (!session.accessToken) throw error;
    // Standalone chat can use its bearer while history stays locked; website sign-in retries.
    console.warn('History key unavailable; continuing with the authenticated session');
  }
  return answer(session);
}

function forgetSession(): Promise<void> {
  latestLookup++;
  return noteSession(null);
}

/** A refused request means the session is gone; a lookup already in flight answers for the past. */
export function noteUnauthorized(): void {
  void forgetSession();
}

// Other tabs hear about an in-chat sign-in or sign-out through the `storage` event, which never
// fires in the tab that wrote; every tab shares the one session, so each rechecks at once.
export function sessionChangeKey(tenant: string): string {
  return `bazak-session-changed:${tenant}`;
}

function announceSessionChange(tenant: string): void {
  safeLocalStorage.setItem(sessionChangeKey(tenant), String(Date.now()));
}

/** Forgets the history key first, then ends the standalone session on the server; a refused
 *  request still throws so the caller can say so, and the next lookup restores what survived. */
export async function signOut(tenant: string): Promise<void> {
  await forgetSession();
  const response = await fetchWithTimeout({
    url: browserAuthPath({ tenant, action: 'signout' }),
    options: { method: 'POST', headers: bindingHeaders(tenant) },
    timeout: WEBSITE_SESSION_TIMEOUT_MS,
    retries: 0,
  });
  if (!response.ok) throw new Error(`Sign-out failed with ${response.status}`);
  // A lookup that started while the request was in flight still saw the session and may have
  // re-installed the key; only now is the server's answer final.
  await forgetSession();
  announceSessionChange(tenant);
}

/** One fresh look at the session, keeping what we hold when the endpoint cannot answer. */
export async function checkSession(tenant: string): Promise<void> {
  try {
    await lookupSession({ tenant, fresh: true });
  } catch {
    // Unreachable or failing endpoint: neither a sign-in nor a sign-out.
  }
}

export async function isSignedIn(tenant: string): Promise<boolean> {
  try {
    return Boolean((await lookupSession({ tenant })).signedIn);
  } catch {
    return false;
  }
}

// Propagate lookup failures; treating them as signed-out would prompt an authenticated guest for another code.
export async function fetchAuthorization(tenant: string): Promise<string | undefined> {
  // This same-origin server boundary supplies OAuth itself; browser code never needs its bearer.
  if (isWebsiteBrowser(tenant)) return undefined;
  const { accessToken } = await lookupSession({ tenant });
  return accessToken ? `Bearer ${accessToken}` : undefined;
}

// Only the verification nonce and lifetime reach the form; state and PKCE stay server-side.
export type BrowserSignInAttempt =
  | { verificationId: string; expiresInSeconds: number; authorizeUrl?: never }
  | { authorizeUrl: string; verificationId?: never; expiresInSeconds?: never };

export async function startSignIn({
  tenant,
  gate,
  threadId,
  distinctId,
}: {
  tenant: string;
  gate?: GatedCall;
  threadId?: string;
  distinctId?: string;
}): Promise<BrowserSignInAttempt | undefined> {
  const response = await fetch(browserAuthPath({ tenant, action: 'signin' }), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ gate, threadId, distinctId }),
  });
  if (!response.ok) return undefined;
  const { verificationId, expiresInSeconds, binding, authorizeUrl } = (await response.json()) as {
    verificationId?: string;
    expiresInSeconds?: number;
    binding?: string;
    authorizeUrl?: string;
  };
  if (authorizeUrl && URL.canParse(authorizeUrl)) {
    if (binding) rememberBinding({ tenant, binding });
    return { authorizeUrl };
  }
  // Without a lifetime, the form could keep accepting codes for an expired attempt.
  if (!verificationId || !Number.isFinite(expiresInSeconds)) return undefined;
  if (binding) rememberBinding({ tenant, binding });
  return { verificationId, expiresInSeconds: expiresInSeconds as number };
}

// The server reconstructs the callback; the browser supplies no destination.
export async function completeSignIn({
  tenant,
  verificationId,
}: {
  tenant: string;
  verificationId: string;
}): Promise<boolean> {
  const response = await fetch(browserAuthPath({ tenant, action: 'complete' }), {
    method: 'POST',
    headers: { ...bindingHeaders(tenant), 'Content-Type': 'application/json' },
    body: JSON.stringify({ verificationId }),
  });
  if (response.ok) announceSessionChange(tenant);
  return response.ok;
}
